import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Clock, AlertCircle, CheckCircle, User, Edit, Trash2 } from 'lucide-react';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Activity {
  id: number;
  activity_type: 'comment' | 'status_change' | 'priority_change' | 'assignment' | 'creation';
  content: string;
  metadata: any;
  created_at: string;
  activityUser: {
    id: number;
    name: string;
    email: string;
  };
}

interface FeedbackActivityHistoryProps {
  feedbackId: number;
  onActivityAdded?: () => void;
}

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'comment':
      return <MessageSquare className="w-4 h-4" />;
    case 'status_change':
      return <AlertCircle className="w-4 h-4" />;
    case 'priority_change':
      return <AlertCircle className="w-4 h-4" />;
    case 'creation':
      return <CheckCircle className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

const getActivityColor = (type: string) => {
  switch (type) {
    case 'comment':
      return 'bg-blue-50 border-blue-200';
    case 'status_change':
      return 'bg-yellow-50 border-yellow-200';
    case 'priority_change':
      return 'bg-orange-50 border-orange-200';
    case 'creation':
      return 'bg-green-50 border-green-200';
    default:
      return 'bg-gray-50 border-gray-200';
  }
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const FeedbackActivityHistory: React.FC<FeedbackActivityHistoryProps> = ({
  feedbackId,
  onActivityAdded
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  const loadActivities = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/feedback-activities/feedback/${feedbackId}/activities`);
      if (response.success) {
        setActivities(response.activities);
      }
    } catch (error) {
      console.error('Erro ao carregar atividades:', error);
    } finally {
      setLoading(false);
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;

    try {
      setSubmitting(true);
      const response = await apiService.post(`/feedback-activities/feedback/${feedbackId}/comments`, {
        content: comment.trim()
      });

      if (response.success) {
        setComment('');
        await loadActivities();
        onActivityAdded?.();
      }
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteComment = async (activityId: number) => {
    if (!confirm('Tem certeza que deseja deletar este comentário?')) return;

    try {
      const response = await apiService.delete(`/feedback-activities/activities/${activityId}`);
      if (response.success) {
        await loadActivities();
      }
    } catch (error) {
      console.error('Erro ao deletar comentário:', error);
    }
  };

  useEffect(() => {
    loadActivities();
  }, [feedbackId]);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd 'de' MMM 'de' yyyy, HH:mm", { locale: ptBR });
    } catch {
      return new Date(dateString).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <MessageSquare className="w-5 h-5" />
          <span>Histórico de Atividades</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Campo para adicionar comentário */}
        <div className="space-y-2">
          <Textarea
            placeholder="Escrever um comentário..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="min-h-[80px]"
            disabled={submitting}
          />
          <div className="flex justify-end">
            <Button
              onClick={addComment}
              disabled={!comment.trim() || submitting}
              size="sm"
              className="flex items-center space-x-2"
            >
              <Send className="w-4 h-4" />
              <span>Enviar</span>
            </Button>
          </div>
        </div>

        {/* Lista de atividades */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              <span className="ml-2">Carregando atividades...</span>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="w-8 h-8 mx-auto mb-2" />
              <p>Nenhuma atividade encontrada</p>
            </div>
          ) : (
            activities.map((activity) => (
              <div
                key={activity.id}
                className={`p-3 rounded-lg border ${getActivityColor(activity.activity_type)}`}
              >
                <div className="flex items-start space-x-3">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="text-xs">
                      {getInitials(activity.activityUser.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                                         <div className="flex items-center space-x-2 mb-1">
                       <span className="font-medium text-sm">{activity.activityUser.name}</span>
                       <span className="text-xs text-gray-500">
                         {formatDate(activity.created_at)}
                       </span>
                      {activity.activity_type === 'comment' && (
                        <Badge variant="secondary" className="text-xs">
                          Comentário
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <div className="mt-1">
                        {getActivityIcon(activity.activity_type)}
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {activity.content}
                      </p>
                    </div>

                    {/* Ações para comentários */}
                    {activity.activity_type === 'comment' && (
                      <div className="flex items-center space-x-2 mt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
                        >
                          Responder
                        </Button>
                                                 {(user?.id === activity.activityUser.id || user?.role === 'admin') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-red-500 hover:text-red-700"
                            onClick={() => deleteComment(activity.id)}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Deletar
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
